import Loading from '@justgiving/loading';
import Alert from '@justgiving/alert';
import Modal from '@justgiving/modal';
import Svg from '@justgiving/svg';
import React, { Component } from 'react';
import { collectionLocationId, features as constFeatures } from 'constants';

import { Field } from 'redux-form';
import FormError from 'components/Alert/FormError';
import { PropTypes } from 'prop-types';
import { Trans } from 'react-i18next';
import pictoEmail from '@justgiving/icons/src/picto-email.svg';

export class NotificationsForm extends Component {
  static propTypes = {
    fetchData: PropTypes.func.isRequired,
    trackPageView: PropTypes.func.isRequired,
    trackLinkClick: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    resetAppErrors: PropTypes.func.isRequired,
    fetchConsentStatements: PropTypes.func.isRequired,
    updateUserConsent: PropTypes.func.isRequired,
    closeConsentModal: PropTypes.func.isRequired,
    userConsent: PropTypes.bool,
    isOptionSelected: PropTypes.bool,
    submitHandler: PropTypes.func.isRequired,
    consentStatementsStatus: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
    }),
    submitting: PropTypes.bool,
    submitSucceeded: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    initialValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    consents: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    showConsentModal: PropTypes.bool,
    consentStatements: PropTypes.array, // eslint-disable-line react/forbid-prop-types
    status: PropTypes.shape({
      isFetching: PropTypes.bool.isRequired,
      fetchError: PropTypes.bool.isRequired,
      postError: PropTypes.bool.isRequired,
      errorMessage: PropTypes.string,
    }).isRequired,
    features: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  };

  static defaultProps = {
    submitting: false,
    submitSucceeded: false,
    error: false,
    initialValues: {},
    features: constFeatures,
    consents: {},
    showConsentModal: false,
    consentStatements: {},
    consentStatementsStatus: {},
    userConsent: false,
    isOptionSelected: false,
  };

  static renderField({ input, label, type, disabled = false }) {
    return (
      <div className="jg-form-group notifications__field row">
        <div className="col-xs-9 col-md-10">{label}</div>
        <div className="col-xs-3 col-md-2 notifications__field--switch">
          <input
            onChange={input.onChange}
            disabled={disabled}
            id={input.name}
            className="jg-ios-switch"
            type={type}
            {...input}
          />
          {/* eslint-disable jsx-a11y/label-has-for */}
          <label
            className="jg-notifications-switch"
            data-off="off"
            data-on="on"
            htmlFor={input.name}
          />
        </div>
      </div>
    );
  }

  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSubmitConsent = this.onSubmitConsent.bind(this);
    this.handleAfterOpen = this.handleAfterOpen.bind(this);
    this.handleConsentChange = this.handleConsentChange.bind(this);
    this.handleModalConfirm = this.handleModalConfirm.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  componentDidMount() {
    const { fetchData, trackPageView } = this.props;
    fetchData();
    trackPageView('notifications', 'notifications');
  }

  componentWillUnmount() {
    this.props.reset();
    this.props.resetAppErrors();
  }

  onSubmit(values = {}) {
    const { trackLinkClick, submitHandler } = this.props;

    // https://github.com/erikras/redux-form/issues/883#issuecomment-216022940
    values.CanContact = !values.CanContact;
    trackLinkClick('toggle', 'notifications', 'save');
    return submitHandler(values);
  }

  onSubmitConsent(consent, index) {
    const { submitHandler, consents } = this.props;

    consents[index].consentGiven = !consents[index].consentGiven;

    return submitHandler({
      consents: [consents[index]],
      transactionId: consents[index].transactionId,
      transactionTypeId: consents[index].transactionTypeId,
      collectionLocationId,
    });
  }

  handleAfterOpen() {
    const { fetchConsentStatements } = this.props;
    fetchConsentStatements();
  }

  handleConsentChange(i, consent, checked) {
    const { updateUserConsent } = this.props;

    consent.consentGiven = checked;
    updateUserConsent(i, consent);
  }

  handleModalConfirm() {
    const { submitHandler, consentStatements } = this.props;

    const consentStatementsUpdated = consentStatements.map(consent => ({
      consentStatementId: consent.id,
      consentStatementText: consent.text,
      consentGiven: consent.consentGiven,
      entityId: 0,
    }));

    return submitHandler({
      consents: consentStatementsUpdated,
      collectionLocationId,
    });
  }

  closeModal() {
    const { closeConsentModal } = this.props;
    closeConsentModal();
  }

  renderFields() {
    const {
      initialValues,
      consents,
      handleSubmit,
      submitting,
      status,
    } = this.props;

    const fields = Object.keys(initialValues).map(key => (
      <Field
        key={`field-${key}`}
        onChange={handleSubmit(
          this.onSubmitConsent.bind(this, initialValues[key], key),
        )}
        name={`${key}`}
        disabled={status.isFetching || submitting || status.fetchError}
        component={NotificationsForm.renderField}
        type="checkbox"
        label={consents[key] ? consents[key].consentStatementText : ''}
        checked={initialValues[key]}
      />
    ));

    return fields;
  }

  renderGDPRModal() {
    const {
      showConsentModal,
      consentStatementsStatus,
      consentStatements,
      isOptionSelected,
      userConsent,
    } = this.props;
    const modalContent = (
      <div>
        <center className="jg-space-mbmd">
          <Svg markup={pictoEmail} className="jg-icon--xlarge" />
          <h1>
            <Trans i18nKey="notificationsForm.GDPR.title">
              Let’s not lose touch
            </Trans>
          </h1>
        </center>
        <p>
          <Trans i18nKey="notificationsForm.GDPR.line1">
            New data protection laws are coming to make your inbox a friendlier
            place. Think farewell spam and hello emails you’re actually
            interested in. Good news, right?
          </Trans>
        </p>
        <p>
          <Trans i18nKey="notificationsForm.GDPR.line2">
            We want to keep sending you the latest tips to help you raise more,
            as well as news about the causes you love. Please select below so we
            can keep in touch.
          </Trans>
        </p>
        <div>
          {consentStatementsStatus.isFetching ? (
            <div className="jg-space-mtlg">
              <Loading size="large" colour="purple" />
            </div>
          ) : (
            <div>
              {consentStatements.map((consent, i) => (
                <div key={`consentWrapper-${consent.id}`}>
                  <p>{consent.text}</p>
                  <input
                    className="jg-radio"
                    type="radio"
                    id={`radio-consent-${consent.id}-yes`}
                    name={`radio-consent-${consent.id}`}
                    value={'true'}
                    onChange={e =>
                      this.handleConsentChange(i, consent, e.target.checked)
                    }
                    checked={isOptionSelected && userConsent}
                  />
                  <label htmlFor={`radio-consent-${consent.id}-yes`}>
                    <Trans i18nKey="notificationsForm.optIn">
                      Yes please, opt me in
                    </Trans>
                  </label>
                  <input
                    className="jg-radio"
                    id={`radio-consent-${consent.id}-no`}
                    type="radio"
                    name={`radio-consent-${consent.id}`}
                    value={'false'}
                    onChange={e =>
                      this.handleConsentChange(i, consent, !e.target.checked)
                    }
                    checked={isOptionSelected && !userConsent}
                  />
                  <label htmlFor={`radio-consent-${consent.id}-no`}>
                    <Trans i18nKey="notificationsForm.optOut">
                      No thanks, opt me out
                    </Trans>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="row">
          <div className="col-lg-10 offset-lg-1">
            <button
              className="jg-btn jg-space-mvmd"
              onClick={this.handleModalConfirm}
              disabled={!isOptionSelected}
            >
              <Trans i18nKey="notificationsForm.continue">Continue</Trans>
            </button>
          </div>
        </div>
      </div>
    );

    const terms = (
      <p>
        <Trans i18nKey="notificationsForm.modalTermsAndPolicy">
          By clicking continue you agree to our
          <a
            href="https://www.justgiving.com/info/privacy"
            rel="noopener noreferrer"
            target="_blank"
            title="This will open in a new tab"
          >
            Privacy policy
          </a>
          and
          <a
            href="https://www.justgiving.com/info/terms-of-service"
            rel="noopener noreferrer"
            target="_blank"
            title="This will open in a new tab"
          >
            Terms of Service
          </a>
        </Trans>
      </p>
    );

    return (
      <Modal
        isOpen={showConsentModal}
        onRequestClose={() => {
          this.closeModal('x');
        }}
        onAfterOpen={this.handleAfterOpen}
        className="gdpr-notifications-modal"
      >
        {modalContent}
        {terms}
      </Modal>
    );
  }

  render() {
    const {
      handleSubmit,
      submitSucceeded,
      submitting,
      status,
      error, // redux-form error
      features,
    } = this.props;

    if (status.isFetching) {
      return (
        <div className="jg-space-mtlg">
          <Loading size="large" colour="purple" />
        </div>
      );
    }

    return (
      <div>
        <form
          method="POST"
          className={`${
            features.preferencesService ? 'gdpr-notifications' : ''
          }`}
          onSubmit={handleSubmit(this.onSubmit)}
        >
          {(status.errorMessage || error) && <FormError error />}
          {submitSucceeded && !status.errorMessage && (
            <Alert type="success" className="qa-message__success jg-space-mbmd">
              <Trans i18nKey="notificationsForm.notificationStatusUpdated">
                Your notification status has been updated.
              </Trans>
            </Alert>
          )}
          <div>
            {features.preferencesService ? (
              <div data-qa="preferencesService-fields">
                {this.renderFields()}
              </div>
            ) : (
              <Field
                onChange={handleSubmit(this.onSubmit)}
                name="CanContact"
                disabled={status.isFetching || submitting || status.fetchError}
                component={NotificationsForm.renderField}
                type="checkbox"
                label="Send me email notifications"
              />
            )}
          </div>
          {/* Button is hidden (for screen readers only). Save happens on Checkbox change */}
          <button
            className="jg-sr-only jg-btn jg-btn--brand qa-button__submit"
            type="submit"
            disabled={status.fetchError}
          >
            <Trans i18nKey="notificationsForm.save">Save</Trans>
          </button>
        </form>
        {features.preferencesService && this.renderGDPRModal()}
      </div>
    );
  }
}

export default NotificationsForm;
